import React, { useEffect } from 'react'
import { Button, Modal, Form, Input, Select, Row, Col } from 'antd'
import { UserOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { get } from 'lodash'

import {
	actLoadRequest,
	actSaveRequest,
	requireRequestById
} from '../../../stores/requests/actions'
import { DO_LOAD, IS_NEW } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { runInAction, toJS } from 'mobx'
import { actLoadRequests } from '../../../stores/ui-lst-requests/actions'
import { CommentsInput } from '../../../components/Form'
import { UserString } from '../../../components/Shared'
import { sendNotification } from '../../../stores/notifications/action'
import { useSmartTranslation } from '../../../cls/TranslationContext'
// TODO, observe visible change
/**
 * @component RequestModal
 * @description Displays a modal form for creating, editing, or viewing a Request item.
 *
 * It integrates form validation, MobX state management, and conditional logic for different interaction modes.
 * Automatically sends a notification to the target user if the request is created for someone other than the creator.
 *
 * @returns {JSX.Element|null} The rendered modal with request form or `null` if no item is selected.
 */
const RequestModal = observer(() => {
	const [form] = Form.useForm()
	const { users, uiLstUsers, uiLstRequests, priorityOptions } = useStores()
	const { loggedInUser } = users
	const { t } = useSmartTranslation()

	const { visible, itmId, mode } = get(
		uiLstRequests,
		['modals', 'request-modal'],
		{
			visible: false,
			itmId: null,
			mode: 'view'
		}
	)

	const _itm = itmId && requireRequestById(itmId)

	useEffect(() => {
		if (itmId && _itm.loaded === DO_LOAD) {
			actLoadRequest(itmId).catch(console.error)
		}
	}, [itmId, visible])

	useEffect(() => {
		if (_itm) {
			form.setFieldsValue({
				createdBy:
					_itm.loaded === IS_NEW
						? loggedInUser.user_id
						: _itm.createdBy,
				name: _itm?.name,
				for_user_id: _itm?.for_user_id || loggedInUser.singleUserId,
				priority: _itm?.priority,
				comments: toJS(_itm?.comments.list || [])
			})
		}
	}, [itmId, _itm?.loaded])

	// Use useWatch to get the current value of "createdBy"
	const createdBy = Form.useWatch('createdBy', form)
	/**
	 * @function onSave
	 * @description Handles form submission. Saves the request to the backend and closes the modal.
	 * Refreshes the request list and optionally sends a notification to the assigned user.
	 *
	 * @param {object} vals - The validated form values.
	 * @returns {void}
	 */
	const notificationKind = _itm?.loaded === IS_NEW ? 'new' : 'update'
	if (!itmId || !loggedInUser?.user_id) return null
	const onSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				// Set creator and timestamp
				_itm.setCreator(vals.createdBy)
				// Reset last full load
				_itm.loaded === IS_NEW && _itm.resetLastFullLoad()

				// Form values
				_itm.setName(vals?.name)
				_itm.setForUserId(vals.for_user_id)
				_itm.setPriority(vals.priority)

				// Comments
				_itm.setComments(vals.comments)
			})
			actSaveRequest(itmId, toJS(_itm.full))
				.then(() => {
					runInAction(() => {
						delete uiLstRequests.modals['request-modal']
					})

					/*
					 * Refresh the requests list with server data
					 * - Requests might come with extra data that needs to be updated in adiacent stores
					 */
					actLoadRequests().catch(console.error)

					// Create notification only if createdBy is not equal to for_user_id
					if (_itm?.for_user_id != _itm.createdBy) {
						const forUser = users.requireUserById(_itm.for_user_id)
						sendNotification(
							loggedInUser,
							forUser?.email,
							_itm?.for_user_id,
							'requests',
							itmId,
							notificationKind
						)
					}
				})
				.catch(console.error)
		}
	}

	const viewOnly = mode === 'view' ? true : false

	// Use mode to conditionally set modal title
	const modalTitle =
		_itm.loaded !== IS_NEW
			? mode === 'data'
				? t('__edit%_request')
				: t('__view%_request')
			: t('__add%_request')

	const for_user_id_options = [
		// Optionally include the logged-in user at the beginning
		loggedInUser.user_id,
		// Combine clients and team arrays, filter duplicates and exclude the logged-in user
		...[
			...new Set([
				...loggedInUser.clients,
				...loggedInUser.team,
				...[...new Set(loggedInUser.isSuperAdmin ? uiLstUsers.ids : [])]
			])
		].filter((user_id) => user_id !== loggedInUser.user_id)
	].map((user_id) => {
		return {
			label: (
				<>
					{user_id === loggedInUser.user_id && (
						<>
							<UserOutlined />{' '}
						</>
					)}
					<UserString
						userId={user_id}
						path={['name']}
						include_full={true}
					/>
					{user_id === loggedInUser.user_id ? ' (self request)' : ''}
				</>
			),
			value: user_id
		}
	})

	return (
		<Modal
			title={modalTitle}
			open={visible}
			onCancel={() => {
				runInAction(() => {
					delete uiLstRequests.modals['request-modal']
				})
			}}
			footer={null}
		>
			<Form
				form={form}
				style={{ width: '100%', marginTop: 25 }}
				layout="vertical"
				onFinish={onSave}
				className={viewOnly && 'view-only-styles'}
			>
				<Row style={{ width: '100%' }}>
					<Col span={12}>
						<Form.Item
							label={t('__by%_user.single')}
							tooltip={t('single.identify-requester')}
							name="createdBy"
						>
							<UserString
								userId={createdBy ?? loggedInUser.user_id}
								path={['name']}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label={t('single.for-user.label')}
							placeholder={t('single.for-user.label')}
							tooltip={t('__send-to-specific%_request%_user')}
							name="for_user_id"
							disabled={_itm.loaded !== IS_NEW}
							rules={[
								{
									required: true,
									message: t('__select.please%_user')
								}
							]}
						>
							<Select
								disabled={_itm.loaded !== IS_NEW || viewOnly}
								placeholder={t('__select.item%_user')}
								allowClear
								options={for_user_id_options}
							/>
						</Form.Item>
					</Col>
				</Row>
				<hr></hr>
				<Form.Item
					label={t('single.subject')}
					name="name"
					tooltip={t('single.subject')}
					rules={[
						{
							required: true,
							message: t('__required%single.subject')
						}
					]}
				>
					<Input disabled={viewOnly} />
				</Form.Item>
				<Form.Item
					label={t('single.filter.priority-desc')}
					tooltip={{
						title: t('single.filter.priority-desc'),
						icon: <InfoCircleOutlined />
					}}
					name="priority"
					rules={[
						{
							required: true,
							message: t('single.priority-required')
						}
					]}
				>
					<Select
						disabled={viewOnly}
						placeholder={t('single.priority-select')}
						allowClear
						style={{
							minWidth: '200px'
						}}
						options={priorityOptions}
					/>
				</Form.Item>
				<Form.Item
					label={t('_comment.multiple')}
					name="comments"
					tooltip={t('__add-allowed%_comment.multiple%_request')}
				>
					<CommentsInput createdBy={loggedInUser.user_id} />
				</Form.Item>
				<Form.Item>
					<Button type="primary" htmlType="submit">
						{_itm.loaded !== IS_NEW
							? t('single.update')
							: t('__add')}
					</Button>
				</Form.Item>
			</Form>
		</Modal>
	)
})

export default RequestModal
